import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Web as WebIcon,
  Psychology as ReasoningIcon,
  AutoAwesome as SynthesizeIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { reasoningApi } from '../../services/api';

const STEP_ICONS = {
  search: <SearchIcon />,
  web: <WebIcon />,
  psychology: <ReasoningIcon />,
  auto_awesome: <SynthesizeIcon />
};

const STEP_COLORS = {
  primary: 'primary',
  info: 'info',
  secondary: 'secondary',
  success: 'success'
};

const StepTypeSelector = ({ 
  open, 
  onClose, 
  onSelectStepType, 
  stepNumber,
  availableStepTypes = [] 
}) => {
  const theme = useTheme();
  const [selectedType, setSelectedType] = useState(null);
  const [customQuery, setCustomQuery] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [stepTypes, setStepTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && availableStepTypes.length === 0) {
      fetchStepTypes();
    } else {
      setStepTypes(availableStepTypes);
    }
  }, [open, availableStepTypes]);

  const fetchStepTypes = async () => {
    setLoading(true);
    setError(null);

    try {
      const types = await reasoningApi.getStepTypes();
      setStepTypes(types);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelect = (stepType) => {
    setSelectedType(stepType);
    setCustomQuery(stepType.default_prompt_template.replace('{topic}', '[specify topic]'));
    setCustomDescription(`${stepType.name}: ${stepType.description}`);
  };

  const handleConfirm = () => {
    if (!selectedType) return;

    const newStep = {
      step: stepNumber,
      action: selectedType.action,
      query: customQuery,
      description: customDescription,
      parameters: { ...selectedType.typical_parameters },
      depends_on: [],
      reasoning_context: `Added ${selectedType.name} step for enhanced reasoning`
    };

    onSelectStepType(newStep);
    handleClose();
  };

  const handleClose = () => {
    setSelectedType(null);
    setCustomQuery('');
    setCustomDescription('');
    setError(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <AddIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">Add Reasoning Step</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a step type for step {stepNumber}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Loading step types...</Typography>
          </Box>
        ) : (
          <>
            {/* Step Type Selection */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
              Select Step Type:
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {stepTypes.map((stepType) => (
                <Grid item xs={12} sm={6} key={stepType.action}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedType?.action === stepType.action 
                        ? `2px solid ${theme.palette.primary.main}` 
                        : undefined,
                      '&:hover': {
                        boxShadow: 2
                      }
                    }}
                    onClick={() => handleTypeSelect(stepType)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: `${STEP_COLORS[stepType.color]}.main`,
                            width: 32,
                            height: 32
                          }}
                        >
                          {STEP_ICONS[stepType.icon]}
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold">
                          {stepType.name}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {stepType.description}
                      </Typography>
                      
                      <Chip 
                        label={stepType.action} 
                        size="small" 
                        color={STEP_COLORS[stepType.color]}
                        variant="outlined"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Customization Section */}
            {selectedType && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Customize Step:
                </Typography>
                
                <TextField
                  fullWidth
                  label="Query/Prompt"
                  multiline
                  rows={3}
                  value={customQuery}
                  onChange={(e) => setCustomQuery(e.target.value)}
                  sx={{ mb: 2 }}
                  helperText="Specify what this step should accomplish"
                />
                
                <TextField
                  fullWidth
                  label="Description"
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  helperText="Brief description of this step's purpose"
                />
                
                {Object.keys(selectedType.typical_parameters).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Default Parameters:
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        bgcolor: 'grey.100',
                        p: 1,
                        borderRadius: 1,
                        fontSize: '0.8rem'
                      }}
                    >
                      {JSON.stringify(selectedType.typical_parameters, null, 2)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleConfirm}
          disabled={!selectedType || !customQuery.trim()}
          sx={{ borderRadius: 2 }}
        >
          Add Step
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StepTypeSelector;
